import { Alert, Button, Field, Table, TableBody, TableCell, TableHeader, TableRow } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import { type JSX, useEffect } from 'react';
import { type Resolver, useForm, useWatch } from 'react-hook-form';

import { listCalendarEntries } from '../../../calendar/api';
import { OperatingUnitSelect } from '../../../components/operating-unit-select';
import { listSites, searchAllCustomers } from '../../../customers/api';
import { listServiceTypes, searchAllMenus } from '../../../menus/api';
import type { OrderItem } from '../../../orders/api';
import { formatCalendarDate } from '../../../orders/format-calendar-date';
import { orderFormSchema, type OrderFormValues } from '../../../orders/order-form-schema';
import { useCustomerUnitIds } from '../../../orders/use-customer-unit-ids';
import { OrderItemEditor, type OrderItemEditorRow } from './order-item-editor';

export interface OrderFormIdentity {
  customerName: string;
  siteName: string;
  serviceDate: string;
}

export interface OrderFormProps {
  mode: 'create' | 'edit';
  /** Only for `mode: 'edit'`: the frozen customer/site/date, rendered read-only. */
  identity?: OrderFormIdentity;
  defaultValues: OrderFormValues;
  /** Items frozen before `rotating-menu` with no position — shown read-only, never resent (`order-form-schema.ts`'s `legacyOrderItems`). */
  legacyItems?: OrderItem[];
  submitting: boolean;
  submitLabel: string;
  submitIcon: LucideIcon;
  formError: string | undefined;
  cutoffError: boolean;
  canOverrideCutoff: boolean;
  onSubmit: (values: OrderFormValues) => void | Promise<void>;
  onCancel?: () => void;
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}

/**
 * `orders/spec.md` → "Alta de pedido"/"Edición de líneas antes del
 * corte"/"Override de corte exitoso con motivo" (UI). Customer → site →
 * service date → one row per position with a published dish that day
 * (`rotating-menu/design.md` §4), grouped by service type — a position
 * without a published dish that day never appears as a row. In
 * `mode: 'edit'` the customer/site/date are frozen (`design.md` §8.1:
 * `OrderUpdateInput` has no fields for them at all) and rendered as
 * read-only text plus hidden inputs, same values the schema still
 * validates. Shared by `OrderFormPage` (create, `/orders/new`) and
 * `OrderDetailPage`'s Detalle tab (edit).
 */
export function OrderForm({
  mode,
  identity,
  defaultValues,
  legacyItems,
  submitting,
  submitLabel,
  submitIcon: SubmitIcon,
  formError,
  cutoffError,
  canOverrideCutoff,
  onSubmit,
  onCancel,
}: OrderFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors },
  } = useForm<OrderFormValues, unknown, OrderFormValues>({
    resolver: zodResolver(orderFormSchema) as unknown as Resolver<
      OrderFormValues,
      unknown,
      OrderFormValues
    >,
    defaultValues,
  });

  const customerId = useWatch({ control, name: 'customerId' });
  const serviceDate = useWatch({ control, name: 'serviceDate' });
  const operatingUnitId = useWatch({ control, name: 'operatingUnitId' });

  // `design.md` T4: with contracts in several points the API needs the point named.
  const customerUnitIds = useCustomerUnitIds(mode === 'create' ? customerId : '');
  const needsOperatingUnit = customerUnitIds.length > 1;

  useEffect(() => {
    if (mode === 'create') {
      setValue('operatingUnitId', '');
    }
  }, [mode, customerId, setValue]);

  function handleValidSubmit(values: OrderFormValues): void | Promise<void> {
    if (needsOperatingUnit && values.operatingUnitId === '') {
      setError('operatingUnitId', { type: 'required', message: 'Seleccioná un punto de venta' });
      return;
    }

    return onSubmit(needsOperatingUnit ? values : { ...values, operatingUnitId: '' });
  }

  const customersQuery = useQuery({
    queryKey: ['customers-for-orders'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
    enabled: mode === 'create',
  });

  const sitesQuery = useQuery({
    queryKey: ['sites-for-orders', customerId],
    queryFn: () => listSites(customerId),
    enabled: mode === 'create' && customerId !== '',
  });

  const calendarQuery = useQuery({
    queryKey: ['calendar-for-order-form', serviceDate],
    queryFn: () => listCalendarEntries({ from: serviceDate, to: serviceDate, isPublished: true }),
    enabled: serviceDate !== '',
  });

  const serviceTypesQuery = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });

  const menusQuery = useQuery({
    queryKey: ['menus-for-orders'],
    queryFn: () =>
      searchAllMenus({
        q: undefined,
        serviceTypeId: undefined,
        dietaryTagId: undefined,
        excludeAllergenId: undefined,
        status: 'ACTIVE',
      }),
  });

  const menusById = Object.fromEntries((menusQuery.data?.items ?? []).map((menu) => [menu.id, menu]));
  const serviceTypeNames = Object.fromEntries(
    (serviceTypesQuery.data ?? []).map((serviceType) => [serviceType.id, serviceType.name]),
  );

  const rows: OrderItemEditorRow[] | undefined = calendarQuery.data?.map((entry) => ({
    positionId: entry.positionId,
    positionName: entry.position.name,
    serviceTypeId: entry.position.serviceTypeId,
    serviceTypeName: serviceTypeNames[entry.position.serviceTypeId] ?? '—',
    displayOrder: entry.position.displayOrder,
    dishName: menusById[entry.menuId]?.name ?? '—',
  }));

  return (
    <form
      onSubmit={(event) => void handleSubmit(handleValidSubmit)(event)}
      className="flex flex-col gap-6"
      noValidate
    >
      {formError !== undefined && formError !== '' ? (
        <Alert tone="danger">{formError}</Alert>
      ) : null}

      {cutoffError ? (
        <Alert tone="warning" title="Ya pasó la hora de corte para esta fecha">
          {canOverrideCutoff ? (
            <div className="flex flex-col gap-3">
              <p>Indicá el motivo para guardar igual el pedido con la excepción registrada.</p>
              <Field
                label="Motivo de la excepción"
                required
                {...register('cutoffOverrideReason')}
                {...maybeError(errors.cutoffOverrideReason?.message)}
              />
            </div>
          ) : (
            <p>
              No tenés permiso para guardar pedidos después del corte. Solicitalo a un
              administrador.
            </p>
          )}
        </Alert>
      ) : null}

      {mode === 'edit' && identity !== undefined ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <input type="hidden" {...register('customerId')} />
          <input type="hidden" {...register('siteId')} />
          <input type="hidden" {...register('serviceDate')} />
          <ReadOnlyField label="Empresa" value={identity.customerName} />
          <ReadOnlyField label="Sede" value={identity.siteName} />
          <ReadOnlyField label="Fecha de servicio" value={formatCalendarDate(identity.serviceDate)} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="order-customer" className="text-sm font-medium text-slate-900">
              Empresa
              <span aria-hidden="true" className="ml-0.5 text-red-600">
                *
              </span>
            </label>
            <select
              id="order-customer"
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
              {...register('customerId')}
            >
              <option value="">Seleccioná una empresa</option>
              {(customersQuery.data?.items ?? []).map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.legalName}
                </option>
              ))}
            </select>
            {errors.customerId?.message === undefined ? null : (
              <p role="alert" className="text-xs text-red-600">
                {errors.customerId.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="order-site" className="text-sm font-medium text-slate-900">
              Sede
              <span aria-hidden="true" className="ml-0.5 text-red-600">
                *
              </span>
            </label>
            <select
              id="order-site"
              disabled={customerId === ''}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50"
              {...register('siteId')}
            >
              <option value="">Seleccioná una sede</option>
              {(sitesQuery.data ?? []).map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
            {errors.siteId?.message === undefined ? null : (
              <p role="alert" className="text-xs text-red-600">
                {errors.siteId.message}
              </p>
            )}
          </div>

          {needsOperatingUnit ? (
            <OperatingUnitSelect
              id="order-operating-unit"
              label="Punto de venta"
              required
              restrictTo={customerUnitIds}
              value={operatingUnitId}
              onChange={(value) => setValue('operatingUnitId', value, { shouldValidate: true })}
              error={errors.operatingUnitId?.message}
            />
          ) : null}

          <Field
            label="Fecha de servicio"
            type="date"
            required
            {...register('serviceDate')}
            {...maybeError(errors.serviceDate?.message)}
          />
        </div>
      )}

      {legacyItems !== undefined && legacyItems.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-slate-900">
            Líneas heredadas (no editables)
          </h2>
          <p className="text-sm text-slate-500">
            Estas líneas se cargaron antes del menú por posiciones y no tienen una posición
            asociada: se muestran solo a modo de referencia y no se reenvían al guardar.
          </p>
          <Table>
            <caption className="sr-only">Líneas heredadas del pedido</caption>
            <thead>
              <tr>
                <TableHeader>Menú</TableHeader>
                <TableHeader>Cantidad</TableHeader>
              </tr>
            </thead>
            <TableBody>
              {legacyItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.menuNameSnapshot}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-slate-900">Líneas del pedido</h2>
        {serviceDate === '' ? (
          <p className="text-sm text-slate-500">
            Elegí una fecha de servicio para ver los menús disponibles.
          </p>
        ) : (
          <OrderItemEditor control={control} register={register} rows={rows} errors={errors.items} />
        )}
        {errors.items?.root?.message === undefined ? null : (
          <p role="alert" className="text-xs text-red-600">
            {errors.items.root.message}
          </p>
        )}
      </div>

      <Field
        label="Notas para el cliente"
        {...register('clientNotes')}
        {...maybeError(errors.clientNotes?.message)}
      />
      <Field
        label="Notas internas"
        {...register('internalNotes')}
        {...maybeError(errors.internalNotes?.message)}
      />

      <div className="flex items-center justify-end gap-3">
        {onCancel === undefined ? null : (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button type="submit" icon={SubmitIcon} loading={submitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}
